/**
 * Session Persistence Helper for Auth0
 * Handles session recovery in private browsers and Safari
 */

export class SessionPersistence {
  static SESSION_KEY = 'auth0_session_backup'
  static EXPIRY_KEY = 'auth0_session_expiry'

  /**
   * Store minimal session info for recovery
   */
  static storeSession(auth0Instance) {
    try {
      if (
        !auth0Instance ||
        !auth0Instance.isAuthenticated ||
        !auth0Instance.user
      ) {
        return false
      }

      const sessionData = {
        isAuthenticated: true,
        user: auth0Instance.user,
        timestamp: Date.now()
      }

      // Store with 24 hour expiry
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000

      localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData))
      localStorage.setItem(this.EXPIRY_KEY, expiryTime.toString())

      return true
    } catch (error) {
      console.warn('Failed to store session data:', error)
      return false
    }
  }

  /**
   * Retrieve and validate stored session
   */
  static getStoredSession() {
    try {
      const expiryTime = localStorage.getItem(this.EXPIRY_KEY)
      const sessionData = localStorage.getItem(this.SESSION_KEY)

      if (!expiryTime || !sessionData) {
        return null
      }

      // Check if expired
      if (Date.now() > parseInt(expiryTime)) {
        this.clearStoredSession()
        return null
      }

      return JSON.parse(sessionData)
    } catch (error) {
      console.warn('Failed to retrieve session data:', error)
      this.clearStoredSession()
      return null
    }
  }

  /**
   * Clear stored session data
   */
  static clearStoredSession() {
    try {
      localStorage.removeItem(this.SESSION_KEY)
      localStorage.removeItem(this.EXPIRY_KEY)
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Check if browser supports proper session storage
   */
  static supportsSessionStorage() {
    try {
      const test = 'sessionStorageTest'
      sessionStorage.setItem(test, 'test')
      sessionStorage.removeItem(test)
      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Detect if running in private/incognito mode
   */
  static async isPrivateMode() {
    try {
      // Try to detect private mode
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate()
        return estimate.quota < 120000000 // Less than 120MB usually indicates private mode
      }

      // Fallback detection for Safari
      if (window.safari && window.safari.pushNotification) {
        return !this.supportsSessionStorage()
      }

      return false
    } catch (error) {
      return false
    }
  }
}
